import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';

import { useIsCoarsePointer } from '../hooks/useMediaQuery';
import { MIN_SPINNER_MS, PULL_THRESHOLD_PX, pullDistance } from '../lib/pull-to-refresh';

import s from './PullToRefresh.module.css';

interface PullToRefreshProps {
  children: ReactNode;
  /** Default: invalidate every active query on the nearest QueryClient. */
  onRefresh?: () => Promise<unknown>;
  /** The shell passes `isMobile`; a coarse pointer is required as well. */
  enabled?: boolean;
  /** Scroll container whose top edge starts a pull. Default: the document. */
  scrollRef?: RefObject<HTMLElement | null>;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

/**
 * Pull down at the top of a list to refetch it.
 *
 * Touch events, not pointer events: a pan on an ordinary scrolling surface
 * makes the browser cancel the pointer stream, so `touchmove` with
 * `passive: false` is the only stream that survives — and it is attached by
 * hand because React registers touch listeners as passive. `preventDefault`
 * is called only once a pull has actually engaged (at scrollTop 0, moving
 * down), so ordinary scrolling is never slowed by it. Renders its children
 * untouched on a fine pointer or when disabled.
 */
export function PullToRefresh({
  children,
  onRefresh,
  enabled = true,
  scrollRef,
}: PullToRefreshProps): JSX.Element {
  const queryClient = useQueryClient();
  const coarse = useIsCoarsePointer();
  const active = enabled && coarse;
  const rootRef = useRef<HTMLDivElement>(null);
  const indicatorRef = useRef<HTMLDivElement>(null);
  const [refreshing, setRefreshing] = useState(false);
  const latest = useRef({ onRefresh, scrollRef, refreshing });
  latest.current = { onRefresh, scrollRef, refreshing };

  useEffect(() => {
    const root = rootRef.current;
    if (!root || !active) return undefined;

    let startY: number | null = null;
    let pulling = false;
    let distance = 0;

    const scroller = (): HTMLElement | null =>
      latest.current.scrollRef?.current ?? (document.scrollingElement as HTMLElement | null);

    const paint = (px: number, ready: boolean): void => {
      const indicator = indicatorRef.current;
      if (!indicator) return;
      indicator.style.transform = `translate(-50%, ${px}px)`;
      indicator.style.opacity = px > 0 ? '1' : '0';
      indicator.dataset['ready'] = ready ? 'true' : 'false';
    };

    const reset = (): void => {
      startY = null;
      pulling = false;
      distance = 0;
      paint(0, false);
    };

    const onTouchStart = (event: TouchEvent): void => {
      if (latest.current.refreshing) return;
      const touch = event.touches[0];
      if (touch === undefined) return;
      if ((scroller()?.scrollTop ?? 0) > 0) return;
      startY = touch.clientY;
      pulling = false;
      distance = 0;
    };

    const onTouchMove = (event: TouchEvent): void => {
      if (startY === null) return;
      const touch = event.touches[0];
      if (touch === undefined) return;
      const dy = touch.clientY - startY;
      if (!pulling) {
        if (dy <= 0 || (scroller()?.scrollTop ?? 0) > 0) {
          startY = null;
          return;
        }
        pulling = true;
      }
      if (event.cancelable) event.preventDefault();
      distance = pullDistance(dy);
      paint(distance, distance >= PULL_THRESHOLD_PX);
    };

    const onTouchEnd = (): void => {
      if (!pulling) {
        reset();
        return;
      }
      const ready = distance >= PULL_THRESHOLD_PX;
      reset();
      if (!ready) return;
      paint(PULL_THRESHOLD_PX / 2, true);
      setRefreshing(true);
      const refresh = latest.current.onRefresh ?? (() => queryClient.invalidateQueries());
      void Promise.allSettled([refresh(), sleep(MIN_SPINNER_MS)]).then(() => {
        setRefreshing(false);
        paint(0, false);
      });
    };

    root.addEventListener('touchstart', onTouchStart, { passive: true });
    root.addEventListener('touchmove', onTouchMove, { passive: false });
    root.addEventListener('touchend', onTouchEnd);
    root.addEventListener('touchcancel', reset);
    return () => {
      root.removeEventListener('touchstart', onTouchStart);
      root.removeEventListener('touchmove', onTouchMove);
      root.removeEventListener('touchend', onTouchEnd);
      root.removeEventListener('touchcancel', reset);
    };
  }, [active, queryClient]);

  if (!active) return <>{children}</>;

  return (
    <div ref={rootRef} className={s.root}>
      <div ref={indicatorRef} className={s.indicator} aria-hidden="true">
        <span className={refreshing ? s.spinnerActive : s.spinner} />
      </div>
      {refreshing ? (
        <span className={s.srOnly} role="status">
          Refreshing…
        </span>
      ) : null}
      {children}
    </div>
  );
}
